import React from "react";
import { useNavigate } from "react-router-dom";

export default function HypTerms() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button onClick={() => navigate("/")} className="flex items-center">
            <h1 className="text-2xl font-bold" style={{ color: '#30853A' }}>Plexys</h1>
          </button>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-2">
          <span className="bg-purple-100 text-purple-800 text-xs font-semibold px-3 py-1 rounded-full">
            iOS App
          </span>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">HYP Terms of Use</h1>
        <p className="text-lg text-gray-500 mb-2">Daily Hypnosis — iOS App</p>
        <p className="text-sm text-gray-500 mb-8">
          Effective Date: February 11, 2026 · Last Updated: February 11, 2026
        </p>

        <div className="prose prose-lg max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700">
              By downloading, installing, or using HYP ("the App"), you agree to be bound by these Terms of Use. 
              If you do not agree to these terms, do not use the App.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700">
              HYP is a wellness application that provides pre-recorded hypnosis and mindfulness audio sessions 
              designed to help users build positive habits and reduce unwanted behaviors. The App offers 
              20 behavior programs with guided audio content across multiple session stages.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Not Medical Advice</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-gray-700">
                <strong>HYP is a wellness tool, not a medical device or treatment.</strong> The content provided 
                in the App is for general wellness and relaxation purposes only. It is not intended to diagnose, 
                treat, cure, or prevent any disease or medical condition. If you have a medical or mental health 
                condition, consult a qualified healthcare professional before using the App. Do not use HYP as a 
                substitute for professional medical advice, diagnosis, or treatment.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Safety Warnings</h2>
            <p className="text-gray-700 mb-3">For your safety:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Do not use HYP while driving, operating machinery, or performing any activity that requires alertness</li>
              <li>Use the App in a safe, comfortable environment where you can relax without interruption</li>
              <li>If you experience any discomfort, dizziness, or adverse effects, stop using the App immediately</li>
              <li>Individuals with epilepsy, seizure disorders, or severe mental health conditions should consult a physician before use</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Subscriptions and Purchases</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Auto-Renewable Subscriptions</h3>
            <p className="text-gray-700 mb-3">HYP offers the following subscription options:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Monthly:</strong> $4.99/month, billed monthly</li>
              <li><strong>Yearly:</strong> $39.99/year, billed annually</li>
              <li><strong>Lifetime:</strong> $74.99, one-time purchase</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Billing</h3>
            <p className="text-gray-700 mb-3">
              Payment will be charged to your Apple ID account at confirmation of purchase. 
              Subscriptions automatically renew unless auto-renew is turned off at least 24 hours 
              before the end of the current period. Your account will be charged for renewal within 
              24 hours prior to the end of the current period at the same price.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Managing Subscriptions</h3>
            <p className="text-gray-700">
              You can manage and cancel your subscription at any time through your Apple ID Account Settings 
              on your device (Settings → Apple ID → Subscriptions). Cancellation takes effect at the end of 
              the current billing period. No refunds are provided for partial billing periods.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Free Sessions</h3>
            <p className="text-gray-700">
              HYP provides a limited number of free sessions to allow you to experience the App before 
              subscribing. Access to unlimited sessions requires an active subscription or lifetime purchase.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property</h2>
            <p className="text-gray-700">
              All content in the App — including audio recordings, scripts, graphics, and software — 
              is owned by or licensed to HYP and is protected by copyright and intellectual property laws. 
              You may not reproduce, distribute, modify, or create derivative works from any content 
              without our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. User Conduct</h2>
            <p className="text-gray-700 mb-3">You agree not to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Reverse engineer, decompile, or disassemble the App</li>
              <li>Record, redistribute, or share the audio content</li>
              <li>Use the App for any unlawful purpose</li>
              <li>Attempt to circumvent any security features or subscription requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Disclaimer of Warranties</h2>
            <p className="text-gray-700">
              The App is provided "as is" and "as available" without warranties of any kind, 
              either express or implied. We do not guarantee that the App will be uninterrupted, 
              error-free, or that any specific results will be achieved from using the App. 
              Individual results from hypnosis and mindfulness sessions may vary.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700">
              To the maximum extent permitted by law, HYP and its developers shall not be liable for 
              any indirect, incidental, special, consequential, or punitive damages arising out of or 
              related to your use of the App, including but not limited to any health outcomes or 
              behavioral changes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to Terms</h2>
            <p className="text-gray-700">
              We reserve the right to modify these Terms of Use at any time. Continued use of the App 
              after changes constitutes acceptance of the modified terms. We will update the "Last Updated" 
              date when changes are made.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Governing Law</h2>
            <p className="text-gray-700">
              These Terms shall be governed by and construed in accordance with the laws of the 
              United States, without regard to conflict of law principles.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 mb-3">
              If you have questions about these Terms of Use, please contact us at:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> support@nsgcode.com<br />
              <strong>Website:</strong>{" "}
              <a href="https://www.plexys.co" className="text-purple-700 hover:underline">
                https://www.plexys.co
              </a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>© {new Date().getFullYear()} HYP. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
